import { useEffect, type ReactNode } from 'react';

import { keepSessionFresh, restoreSession } from '@/features/auth/session-lifecycle';
import { useSessionStore } from '@/shared/api';

const REFRESH_CHECK_MS = 10 * 60 * 1_000;

/**
 * Leest de bewaarde sessie uit IndexedDB voordat de router iets rendert. Zonder
 * deze stap zou een refresh in het veld je naar het inlogscherm gooien - ook
 * zonder netwerk.
 *
 * Daarna, op de achtergrond: `GET /auth/me` werkt rollen en omgevingen bij (een
 * 401 stuurt je naar het inlogscherm), het token wordt verlengd zolang de app
 * open staat, en als de verbinding terugkomt gebeurt dat opnieuw.
 */
export function SessionGate({ children }: { children: ReactNode }) {
    const hydrate = useSessionStore((state) => state.hydrate);
    const hydrated = useSessionStore((state) => state.hydrated);

    useEffect(() => {
        let active = true;

        void hydrate().then((session) => {
            if (active && session) {
                void restoreSession().then(() => keepSessionFresh());
            }
        });

        return () => {
            active = false;
        };
    }, [hydrate]);

    useEffect(() => {
        const refresh = () => {
            if (document.visibilityState === 'visible') {
                void keepSessionFresh();
            }
        };
        const reconnect = () => {
            void restoreSession().then(() => keepSessionFresh());
        };
        const timer = window.setInterval(refresh, REFRESH_CHECK_MS);

        window.addEventListener('online', reconnect);
        document.addEventListener('visibilitychange', refresh);

        return () => {
            window.clearInterval(timer);
            window.removeEventListener('online', reconnect);
            document.removeEventListener('visibilitychange', refresh);
        };
    }, []);

    if (!hydrated) {
        return null;
    }

    return <>{children}</>;
}
